import { getCurrentScreen, getIsUserInitiated } from '../../screenContext';
import { trackingService as baseTrackingService } from '../../services';
import {
  AppTrackingService,
  TrackEventParams,
} from '../useTrackScreenDualFire/useTrackScreenDualFire.types';
import {
  ButtonTapped,
  ButtonTappedPayload,
  ConsentGranted,
  ConsentGrantedPayload,
  ContentViewed,
  ContentViewedPayload,
  ErrorOccurred,
  ErrorOccurredPayload,
  ExperimentExposed,
  ExperimentExposedPayload,
  LocationReceived,
  LocationReceivedPayload,
  LocationRequested,
  LocationRequestedPayload,
  PaymentRequested,
  PaymentRequestedPayload,
  ThemeApplied,
  ThemeAppliedPayload,
  FieldEnteredPayload,
  FormSubmitted,
  FormSubmittedPayload,
  ListItemSelected,
  ListItemSelectedPayload,
  SelectionMade,
  SelectionMadePayload,
  UiVocabulary,
} from './createTrackUiEvents.types';

/**
 * Binds an app's tracking service to the catalogued OAF interaction events, so
 * a call site reports both vocabularies in one line:
 *
 * ```ts
 * trackUi.buttonTapped(
 *   { button_name: 'select_ticket' },
 *   'Park: Select Ticket: Clicked',
 * );
 * ```
 *
 * The first argument is the OAF payload, spelled with the wire property names so
 * the call site shows exactly what lands in Mixpanel; the rest is the legacy call
 * unchanged, keeping its existing per-event type checking. `screen_name` is not
 * among them — it is filled in from the last displayed screen, because a value
 * restated at fifty call sites is a value that drifts from the screen the app
 * actually reported (see `screenContext`).
 *
 * The universal events go to the **base** service and the legacy ones to the
 * app's wrapper. That asymmetry is the point: the wrappers prefix every event
 * with the app's name, and a cross-app funnel needs `button_tapped` to be the
 * same string everywhere. The app is already on the event as a super property.
 *
 * Emitters, not hooks: these fire from event handlers, and a hook cannot.
 */
export default function createTrackUiEvents<
  TVocabulary extends UiVocabulary,
  TEvents = never,
>(
  // Required exactly when the app has legacy events, forbidden when it does
  // not. A plain optional parameter let the two drift apart: an app could
  // declare a real `TEvents` and omit the service, which kept the legacy
  // overload callable while `trackingService?.` silently dropped every legacy
  // event — and `check-tracking-events.mjs` reads call-site literals, so it
  // would have gone on reporting those names as emitted. A dead series that
  // the guard certifies as alive is worse than no guard, so this is a type
  // error rather than something to notice later.
  //
  // `[TEvents] extends [never]` rather than `TEvents extends never`: the bare
  // form distributes over the union and answers for each member instead of the
  // whole.
  ...[trackingService]: [TEvents] extends [never]
    ? []
    : [AppTrackingService<TEvents>]
) {
  /**
   * Sends the universal event, then the legacy one — in that order, so a throw
   * inside the legacy payload cannot cost us the catalogued event too.
   *
   * `legacyEvent` — and the service itself — are optional because some of these
   * have no legacy counterpart to dual-fire: `fieldEntered` below, handyparken's
   * onboarding outcome (FE-2719), and every event in a greenfield mini, which
   * has no legacy series at all (FE-2671). Inventing names to keep the shape
   * symmetrical would mean growing a closed allowlist.
   */
  const emit = <T extends keyof TEvents & string>(
    event: string,
    payload: Record<string, unknown>,
    withScreen: boolean,
    // Whether `withoutUiEvents` may suppress this one. True for everything
    // that claims a user did something; false for `errorOccurred`, which
    // claims nothing of the sort — see the comment on it below.
    gated: boolean,
    legacyEvent?: T,
    ...params: TrackEventParams<TEvents, T>
  ) => {
    const screenName = withScreen ? getCurrentScreen() : null;

    // Suppressed for a navigation nobody tapped; the legacy event below still
    // fires, because it always has. See `withoutUiEvents`.
    if (gated && !getIsUserInitiated()) {
      if (legacyEvent) {
        trackingService?.trackEvent(legacyEvent, ...params);
      }

      return;
    }

    baseTrackingService.trackEvent(event, {
      ...payload,
      // Omitted rather than guessed when nothing has been displayed yet: an
      // absent property is a visible gap in Mixpanel, a wrong one is not.
      ...(screenName ? { screen_name: screenName } : {}),
    });

    if (legacyEvent) {
      trackingService?.trackEvent(legacyEvent, ...params);
    }
  };

  // The implementation signatures are the loose ones, as overloads require:
  // `TrackEventParams` is a *required* one-element tuple for any event carrying
  // a payload, so a one-argument call signature is not assignable to it.
  const cast = (params: unknown[]) =>
    params as TrackEventParams<TEvents, keyof TEvents & string>;

  const buttonTapped: ButtonTapped<TVocabulary, TEvents> = (
    payload: ButtonTappedPayload<TVocabulary>,
    legacyEvent?: keyof TEvents & string,
    ...params: unknown[]
  ) => emit('button_tapped', payload, true, true, legacyEvent, ...cast(params));

  const listItemSelected: ListItemSelected<TVocabulary, TEvents> = (
    payload: ListItemSelectedPayload<TVocabulary>,
    legacyEvent?: keyof TEvents & string,
    ...params: unknown[]
  ) =>
    emit(
      'list_item_selected',
      payload,
      true,
      true,
      legacyEvent,
      ...cast(params),
    );

  const selectionMade: SelectionMade<TVocabulary, TEvents> = (
    payload: SelectionMadePayload<TVocabulary>,
    legacyEvent?: keyof TEvents & string,
    ...params: unknown[]
  ) =>
    emit('selection_made', payload, true, true, legacyEvent, ...cast(params));

  const formSubmitted: FormSubmitted<TVocabulary, TEvents> = (
    payload: FormSubmittedPayload<TVocabulary>,
    legacyEvent?: keyof TEvents & string,
    ...params: unknown[]
  ) =>
    emit('form_submitted', payload, true, true, legacyEvent, ...cast(params));

  // The only one without `screen_name`: consent is granted once and follows
  // the person, not the screen they happened to be on (catalog:
  // `events.consent.consent_granted` declares no location property).
  const consentGranted: ConsentGranted<TVocabulary, TEvents> = (
    payload: ConsentGrantedPayload<TVocabulary>,
    legacyEvent?: keyof TEvents & string,
    ...params: unknown[]
  ) =>
    emit('consent_granted', payload, false, true, legacyEvent, ...cast(params));

  /**
   * An impression: content was shown, without anyone doing anything.
   *
   * Carries `screen_name` like the gesture emitters — a campaign card seen on
   * the carousel and the same campaign seen full-screen are different facts.
   * Unlike them it is *expected* to fire from an effect, which is why the
   * `useEffect` guard in `check-tracking-events.mjs` deliberately does not
   * list it: for a tap that shape is a bug, for an impression it is the only
   * shape there is.
   */
  const contentViewed: ContentViewed<TVocabulary, TEvents> = (
    payload: ContentViewedPayload<TVocabulary>,
    legacyEvent?: keyof TEvents & string,
    ...params: unknown[]
  ) =>
    emit('content_viewed', payload, true, true, legacyEvent, ...cast(params));

  /**
   * A technical or non-terminal error.
   *
   * **The one emitter `withoutUiEvents` cannot suppress, and that is the point
   * rather than an oversight.** Every other universal event here claims a user
   * did something, so suppressing it during a programmatic navigation is
   * correct. An error claims nothing of the sort — and the errors most worth
   * having are precisely the ones raised *inside* such a navigation, since
   * `DeepLink: Invalid` fires from a deep link and nowhere else. Gating it
   * would drop those and drop them silently, on a channel where "no errors
   * reported" and "errors suppressed" look exactly alike.
   *
   * A terminal business failure is not this: rule `failure-boundary` puts it on
   * the business event as `status: failure` + `failure_reason`. Roughly half
   * the legacy names ending in `Failed` fall on that side of the line.
   */
  const errorOccurred: ErrorOccurred<TVocabulary, TEvents> = (
    payload: ErrorOccurredPayload<TVocabulary>,
    legacyEvent?: keyof TEvents & string,
    ...params: unknown[]
  ) =>
    emit('error_occurred', payload, true, false, legacyEvent, ...cast(params));

  /**
   * The five below are ungated for the same reason `errorOccurred` is, and it
   * is not a stylistic choice: none of them claims a user did something.
   *
   * `themeApplied` and `experimentExposed` fire during render — a theme is a
   * device fact and an exposure is a flag read — so a gate would drop them
   * whenever they happened to land inside a `withoutUiEvents` navigation.
   * `locationRequested` / `locationReceived` are a permission round trip, and
   * `paymentRequested` must survive a payment started from a deep link, which
   * is precisely the case `withoutUiEvents` suppresses.
   *
   * None carries `screen_name`, because the catalog declares it on none of
   * them. Adding a property the contract does not list is how a consumer ends
   * up querying something that only sometimes exists.
   */
  const paymentRequested: PaymentRequested<TEvents> = (
    payload: PaymentRequestedPayload,
    legacyEvent?: keyof TEvents & string,
    ...params: unknown[]
  ) => emit('payment_requested', payload, false, false, legacyEvent, ...cast(params));

  const locationRequested: LocationRequested<TEvents> = (
    payload: LocationRequestedPayload,
    legacyEvent?: keyof TEvents & string,
    ...params: unknown[]
  ) => emit('location_requested', payload, false, false, legacyEvent, ...cast(params));

  const locationReceived: LocationReceived<TEvents> = (
    payload: LocationReceivedPayload,
    legacyEvent?: keyof TEvents & string,
    ...params: unknown[]
  ) => emit('location_received', payload, false, false, legacyEvent, ...cast(params));

  const themeApplied: ThemeApplied<TEvents> = (
    payload: ThemeAppliedPayload,
    legacyEvent?: keyof TEvents & string,
    ...params: unknown[]
  ) => emit('theme_applied', payload, false, false, legacyEvent, ...cast(params));

  const experimentExposed: ExperimentExposed<TEvents> = (
    payload: ExperimentExposedPayload,
    legacyEvent?: keyof TEvents & string,
    ...params: unknown[]
  ) => emit('experiment_exposed', payload, false, false, legacyEvent, ...cast(params));

  return {
    buttonTapped,
    paymentRequested,
    locationRequested,
    locationReceived,
    themeApplied,
    experimentExposed,
    listItemSelected,
    selectionMade,
    formSubmitted,
    consentGranted,
    contentViewed,
    errorOccurred,

    // No legacy half anywhere in the tree: nothing has ever reported a field
    // being filled in, so this one is new instrumentation rather than a second
    // name for an existing series. Inventing a legacy counterpart to keep the
    // shape symmetrical would mean adding a name to a closed allowlist.
    fieldEntered: (payload: FieldEnteredPayload<TVocabulary>) => {
      if (!getIsUserInitiated()) {
        return;
      }

      const screenName = getCurrentScreen();

      baseTrackingService.trackEvent('field_entered', {
        ...payload,
        ...(screenName ? { screen_name: screenName } : {}),
      });
    },
  };
}
